import React from 'react';
import Box from '@mui/material/Box';
import { FdaSourceKey } from '../services/api/domains/products/types';
import {
  COLOR_INFO_TEXT,
  COLOR_INFO_BANNER_BG,
  COLOR_INFO_BANNER_BORDER,
} from 'theme/colors';
import { GRAY_500 } from 'theme/grayScale';

const SOURCE_LABELS: Record<FdaSourceKey, string> = {
  ob: 'Orange Book',
  ndc: 'NDC',
  dmf: 'USDMF',
};

export interface FdaSourceUnlinkedBannerProps {
  source: FdaSourceKey;
  onClear: () => void;
}

/**
 * Replaces the FDA Sources search bar/toggle/filter controls while a
 * Navigator-tab "Not Yet Sourceable" metric card deep link is active
 * (docs/features/fda-source-record-browser.md, Task FSU-004). The X
 * clears the unlinked-only filter, restoring normal FDA Sources search —
 * it does not navigate away or change the selected source.
 */
const FdaSourceUnlinkedBanner: React.FC<FdaSourceUnlinkedBannerProps> = ({
  source,
  onClear,
}) => (
  <div style={styles.banner} data-testid="fda-source-unlinked-banner">
    <span style={styles.text}>
      Viewing Not Yet Sourceable products from {SOURCE_LABELS[source]}
    </span>
    <Box
      component="button"
      onClick={onClear}
      sx={{
        // All static values live here, not in a plain `style` object --
        // an inline `style` attribute always wins the cascade over a
        // pseudo-class rule from the same element's `sx`-generated class,
        // which would silently make the :hover/:active rules below inert
        // (Copilot review, PR #832; same root cause as the "LgmButton
        // background shorthand" issue this project has hit before).
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        color: GRAY_500,
        fontSize: '1.125rem',
        lineHeight: 1,
        padding: '0 0.25rem',
        borderRadius: '0.25rem',
        '&:hover': {
          color: COLOR_INFO_TEXT,
          backgroundColor: 'rgba(0, 0, 0, 0.04)',
        },
        '&:active': {
          backgroundColor: 'rgba(0, 0, 0, 0.08)',
        },
        '&:focus-visible': {
          outline: `2px solid ${COLOR_INFO_TEXT}`,
          outlineOffset: '1px',
        },
      }}
      aria-label="Clear unlinked filter and return to FDA Sources search"
      title="Clear filter"
      data-testid="fda-source-unlinked-banner-close">
      ×
    </Box>
  </div>
);

const styles: Record<string, React.CSSProperties> = {
  banner: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    alignItems: 'center',
    gap: '0.375rem',
    padding: '0.375rem 0.75rem',
    backgroundColor: COLOR_INFO_BANNER_BG,
    border: `1px solid ${COLOR_INFO_BANNER_BORDER}`,
    borderRadius: '0.375rem',
  },
  text: {
    flex: 1,
    minWidth: 0,
    fontSize: '0.8125rem',
    color: COLOR_INFO_TEXT,
  },
};

export default FdaSourceUnlinkedBanner;
